import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { NAVIGATION, withFinds } from '@/config/site';
import { showsFinds, useFindsLive } from '@/client/lib/useFindsLive';
import { useCart } from '@/features/cart/CartContext';
import { useUI } from '@/client/ui/UIContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { CartIcon, MenuIcon } from '@/components/Icons';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { CaroWordmark } from '@/components/Logo';
import { lp } from '@/client/i18n/locale';

function isExternal(href: string) {
  return href.startsWith('http');
}

/**
 * Editorial header (Lemaire-style). At the top of the page it is tall: the
 * wordmark sits large and centred with the full navigation — including store
 * categories and the editorial sub-sections — laid out beneath it. Once the
 * visitor scrolls, it condenses to a compact single-row bar (`.is-condensed`).
 */
export function Header() {
  const { count } = useCart();
  const { openCart, toggleNav, navOpen } = useUI();
  const { t } = useI18n();
  const [condensed, setCondensed] = useState(false);
  // «Hallazgos» only while a find is public (hidden while unknown).
  const findsShown = showsFinds(useFindsLive());

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const label = (item: { label: string; key?: string }) =>
    item.key ? t(item.key) : item.label;

  const primaryLinks = NAVIGATION.filter((item) => !item.secondary);

  // Sub-destinations revealed only when the header is expanded (store
  // categories + `secondary` items such as Newsletter) — the "all options"
  // layer that collapses away on scroll.
  const subLinks = [
    ...NAVIGATION.flatMap((item) => withFinds(item.children ?? [], findsShown)),
    ...NAVIGATION.filter((item) => item.secondary),
  ];

  return (
    <header className={`header${condensed ? ' is-condensed' : ''}`}>
      <div className="container header__inner">
        <button
          type="button"
          className="icon-btn nav-toggle"
          aria-label={t('a11y.menuOpen')}
          aria-expanded={navOpen}
          aria-haspopup="dialog"
          onClick={toggleNav}
        >
          <MenuIcon />
        </button>

        <Link to={lp('/')} className="brand" aria-label={t('a11y.homeLink')}>
          <CaroWordmark className="brand__mark" title="CARO." />
          <span className="brand__sub">Casa de estilo</span>
        </Link>

        <div className="header__actions">
          <LanguageSwitcher />
          <button
            type="button"
            className="icon-btn"
            aria-label={`${t('a11y.cart')} (${count})`}
            onClick={openCart}
          >
            <CartIcon />
            {count > 0 ? <span className="cart-count">{count}</span> : null}
          </button>
        </div>
      </div>

      <nav className="header__nav" aria-label={t('a11y.mainNav')}>
        <ul className="header__nav-primary">
          {primaryLinks.map((item) => (
            <li key={item.href}>
              {isExternal(item.href) ? (
                <a href={item.href} target="_blank" rel="noopener noreferrer">
                  {label(item)}
                </a>
              ) : (
                <NavLink
                  to={lp(item.href)}
                  end={item.href === '/'}
                  className={({ isActive }) => (isActive ? 'is-active' : '')}
                >
                  {label(item)}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        {subLinks.length ? (
          <ul className="header__nav-sub" aria-label={t('a11y.sectionsNav')}>
            {subLinks.map((child) => (
              <li key={child.href}>
                <NavLink
                  to={lp(child.href)}
                  className={({ isActive }) => (isActive ? 'is-active' : '')}
                >
                  {label(child)}
                </NavLink>
              </li>
            ))}
          </ul>
        ) : null}
      </nav>
    </header>
  );
}
